import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CalendarPlus,
  UserPlus,
  Stethoscope,
  Banknote,
  CalendarDays,
  Activity,
  CircleCheck,
  Wallet,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Check,
  type LucideIcon,
} from "lucide-react";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { reportsApi, reportsQueryKeys, type ReportAlert } from "../../shared/api/reports";
import { AlertSettingsDialog } from "./AlertSettingsDialog";
import { drillLink, type DrillMetric } from "../../shared/api/drillDown";
import { ACTIVE_BRANCH_KEY, branchesApi, branchesQueryKeys } from "../../shared/api/branches";
import { formatTime, formatVnd, isoDate, shiftIsoDate } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DataTable, type Column } from "../../shared/ui/DataTable";
import type { AppointmentSummary } from "../../shared/types/appointment";
import { AppointmentStatusBadge } from "../appointments/components/AppointmentStatusBadge";
import { useOperatesModule } from "@/shared/hooks/useOperatesModule";
import { usePlanScreens } from "@/shared/screens/usePlanScreens";
import { REVENUE_REPORT_PATH, revenueTodayRange } from "./revenueToday";
import { ReportScopeNotice } from "../../shared/branch/ReportScopeNotice";
import { presetRange } from "../../shared/ui/DateRangeFilter";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/** Báo cáo › Tổng quan vận hành — số liệu theo kỳ của chính những ô "hôm nay" trên màn này. */
const OPERATIONS_REPORT_PATH = "/reports/operations";

const QUICK_ACTIONS: { to: string; icon: LucideIcon; title: string; sub: string }[] = [
  { to: "/appointments/new", icon: CalendarPlus, title: "Đặt lịch mới", sub: "Tạo lịch hẹn cho bệnh nhân" },
  { to: "/patients/new", icon: UserPlus, title: "Bệnh nhân mới", sub: "Mở hồ sơ lần đầu" },
  { to: "/encounters", icon: Stethoscope, title: "Hàng chờ khám", sub: "Bệnh nhân đã check-in" },
  { to: "/invoices", icon: Banknote, title: "Hoá đơn", sub: "Thu phí sau khám" },
];

const RESOURCE_LINKS: { to: string; label: string }[] = [
  { to: "/settings/medicines", label: "Danh mục thuốc" },
  { to: "/prescriptions/templates", label: "Toa thuốc mẫu" },
  { to: "/settings/users", label: "Người dùng & nhân viên" },
  { to: REVENUE_REPORT_PATH, label: "Báo cáo doanh thu" },
];

/**
 * Dashboard = clinic situational awareness at a glance. Optimised for the
 * receptionist's "morning of operations" — today's appointments first, KPIs
 * second, quick actions always within one click.
 *
 * Phân vai với Báo cáo › Tổng quan vận hành (#19898): cùng nguồn số, hai câu hỏi. Màn này là
 * HÔM NAY — "bây giờ phải làm gì" — và có trong mọi gói; số liệu theo kỳ (tỷ lệ, xu hướng, so với kỳ
 * trước) ở Tổng quan vận hành, nối qua nút "Xem theo kỳ". Đừng thêm bộ lọc kỳ vào đây.
 */
export function DashboardPage() {
  const today = isoDate(new Date());
  const todayQuery = useQuery({
    queryKey: appointmentQueryKeys.forDate(today),
    queryFn: () => appointmentsApi.forDate(today),
  });

  // Hôm qua theo LỊCH, không phải "bây giờ − 24h" (#19847).
  const yesterday = shiftIsoDate(new Date(), -1);

  // Danh sách hôm nay chỉ còn nuôi BẢNG "Sắp tới hôm nay" — nó là một danh sách, không phải KPI.
  const todayList = todayQuery.data ?? [];

  // Real revenue from /v1/reports/revenue/daily (yesterday → today; both rows pulled out).
  //
  // ADR-0048 (#18965): một phòng khám chế độ PATIENT_CHANNEL không vận hành Báo cáo trong Addy —
  // endpoint này trả 409 cho họ. Hỏi trước rồi mới gọi, thay vì gọi rồi hứng lỗi: sổ doanh thu
  // của họ nằm ở hệ thống chính, nên đây không phải một lỗi cần hiện, mà là một thẻ không thuộc
  // về màn hình này.
  const { operates: operatesReports, loaded: modeLoaded } = useOperatesModule("REPORTS");
  // ADR-0053 (#19402): vùng Báo cáo mở là chưa đủ — gói Đặt lịch có ba màn báo cáo khác nên vùng
  // mở, mà Báo cáo doanh thu thì không. Hỏi đúng MÀN HÌNH mà số liệu này thuộc về, qua đường dẫn
  // của nó như menu và bộ chặn đường dẫn, không qua mã gói: phòng khám có thể được "chỉnh riêng".
  // Thẻ "Thực thu hôm nay" là link tới báo cáo đó — hiện một con số doanh thu mà link dẫn tới
  // trang 402 là một ngõ cụt ngay trên trang chủ.
  const plan = usePlanScreens();
  // Trên màn này MỌI quyết định vẽ đều kéo theo một lượt gọi API: thẻ KPI đọc `revenueQuery`. Nên
  // ở đây phải CHỜ biết chế độ rồi mới vẽ — vẽ lạc quan đồng nghĩa với bắn request. (Nút ở màn
  // Tiếp đón thì ngược lại: vẽ nó không tốn gì.)
  // `modeLoaded` đã gồm `plan.settled`, nên `allowsPath` ở đây là câu trả lời của máy chủ.
  // #19850 — và người xem phải có quyền đọc tài chính. Bác sĩ và lễ tân mặc định không có
  // report.financial.read; trước đây thẻ vẫn vẽ, endpoint trả 403, và thẻ hiện "0₫" — một con số
  // sai về phòng khám thay vì một thẻ không thuộc về người này.
  const perms = usePermissions();

  // #19859 (STAT-22) — các ô lịch hẹn đọc SỐ CỦA SERVER (`/reports/appointments/summary`), không
  // đếm trên `forDate`: danh sách đó bị server cắt ở 200 dòng (PageableConfig), nên một ngày đông
  // hơn thế từng hiện số sai mà không báo gì. Cùng hai cửa với thẻ doanh thu: chế độ vận hành có
  // REPORTS, và người xem có quyền đọc số vận hành — hoặc "của tôi" (bác sĩ, server tự buộc).
  const canSeeAppointmentKpis =
    perms.loaded && (perms.has("report.operational.read") || perms.has("report.self.read"));
  // `operatesReports` đã gồm vùng REPORTS của GÓI (useOperatesModule → plan.zoneOpen), nên một gói
  // không có màn báo cáo nào không gọi endpoint (sẽ là 402) và không vẽ ô lỗi.
  const showAppointmentKpis = modeLoaded && operatesReports && canSeeAppointmentKpis;
  const summaryQuery = useQuery({
    queryKey: reportsQueryKeys.appointmentsSummary(today, today, "status"),
    queryFn: () => reportsApi.appointmentsSummary(today, today, "status"),
    enabled: showAppointmentKpis,
  });
  const summary = summaryQuery.data;
  const mine = summary?.selfOnly ?? false;
  // #19863 — link mở ĐÚNG tập đã đếm (drill-down): cùng chỉ số, cùng ngày, cùng mốc thời gian của
  // lúc tính, và với số "của tôi" thì cùng bác sĩ mà server đã buộc.
  const listLink = (metric: DrillMetric) => {
    const at = summaryQuery.dataUpdatedAt ? new Date(summaryQuery.dataUpdatedAt) : new Date();
    return drillLink({
      metric,
      fromDate: today,
      toDate: today,
      asOf: today,
      at: at.toISOString(),
      keys: summary?.selfDoctorId ? { doctor: summary.selfDoctorId } : {},
    });
  };
  // #19867 — cảnh báo dựa trên dữ liệu, server tính theo ngưỡng của phòng khám.
  const alertsQuery = useQuery({
    queryKey: reportsQueryKeys.alerts(),
    queryFn: () => reportsApi.alerts(),
    enabled: showAppointmentKpis,
  });
  const [editingThresholds, setEditingThresholds] = useState(false);
  /** Cảnh báo có danh sách → drill-down đúng kỳ của nó; trung vị không có danh sách → báo cáo. */
  const dataAlertLink = (a: ReportAlert, asOf: string) =>
    a.metric
      ? drillLink({
          metric: a.metric as DrillMetric,
          fromDate: a.fromDate,
          toDate: a.toDate,
          asOf,
          keys: summary?.selfDoctorId ? { doctor: summary.selfDoctorId } : {},
        })
      : `/reports/operations?from=${a.fromDate}&to=${a.toDate}`;
  const branches = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
    staleTime: 5 * 60_000,
    enabled: showAppointmentKpis,
  });
  const scopeLabel = branchScopeLabel(branches.data);

  const showRevenue =
    modeLoaded &&
    operatesReports &&
    plan.allowsPath(REVENUE_REPORT_PATH) &&
    perms.loaded &&
    perms.has("report.financial.read");
  const revenueRange = revenueTodayRange();
  const revenueQuery = useQuery({
    queryKey: reportsQueryKeys.revenueDaily(revenueRange.from, revenueRange.to),
    queryFn: () => reportsApi.revenueDaily(revenueRange.from, revenueRange.to),
    // `loaded &&` là phần bắt buộc: thiếu nó thì mọi phòng khám PATIENT_CHANNEL vẫn bắn đúng
    // một request 409 mỗi lần mở Dashboard, trước khi biết chế độ.
    enabled: showRevenue,
  });
  // #18843 — the card says "Thực thu", so it shows money received net of refunds, not the value
  // invoiced. The two diverge whenever a bill is issued today and paid tomorrow, which for a
  // clinic is most of them.
  const revenueToday =
    revenueQuery.data?.find((r) => r.date === today)?.netCollected ?? 0;
  const revenueYesterday =
    revenueQuery.data?.find((r) => r.date === yesterday)?.netCollected ?? 0;
  // #19864: hôm qua = 0 → không có % để so ("—", không ∞); bằng nhau → "0%", không phải "—".
  const revenueDelta =
    revenueYesterday > 0
      ? Math.round(((revenueToday - revenueYesterday) / revenueYesterday) * 100)
      : null;

  // ADR-0053: Tổng quan luôn có trong mọi gói, nhưng các lối tắt của nó thì không — một lối tắt
  // dẫn tới màn hình ngoài gói là mời bấm vào trang 402. Chờ `settled` như menu: hiện rồi rút
  // lại trông như lỗi.
  const inPlan = <T extends { to: string }>(links: T[]) =>
    plan.settled ? links.filter((link) => plan.allowsPath(link.to)) : [];
  // #19902 — lối sang số liệu THEO KỲ, mở sẵn đúng preset "7 ngày" của báo cáo. Cùng ba cửa với
  // menu của trang đó (chế độ vận hành, gói, quyền đọc số vận hành hoặc "của tôi"): một lối đi
  // mà trang đích trả 402/403 là ngõ cụt ngay trên trang chủ.
  const periodRange = presetRange("7d");
  const periodLink =
    showAppointmentKpis && plan.allowsPath(OPERATIONS_REPORT_PATH)
      ? `${OPERATIONS_REPORT_PATH}?from=${periodRange.from}&to=${periodRange.to}`
      : null;
  const quickActions = inPlan(QUICK_ACTIONS);
  const resourceLinks = inPlan(RESOURCE_LINKS);

  const upcomingColumns: Column<AppointmentSummary>[] = [
    {
      key: "time",
      header: "Giờ",
      width: 90,
      render: (a) => <strong>{formatTime(a.startAt)}</strong>,
    },
    {
      key: "patient",
      header: "Bệnh nhân",
      render: (a) => (
        <Link
          to={`/appointments/${a.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-primary hover:underline"
        >
          {a.patientName}
        </Link>
      ),
    },
    { key: "doctor", header: "Bác sĩ", render: (a) => a.doctorName },
    {
      key: "service",
      header: "Dịch vụ",
      render: (a) => (
        <span className="text-muted-foreground">{a.serviceName ?? "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: 160,
      render: (a) => <AppointmentStatusBadge status={a.status} />,
    },
  ];

  // Số "của tôi" thì bảng ngay dưới cũng của tôi — một màn hình không trả lời hai câu hỏi khác nhau.
  const selfDoctorId = summary?.selfOnly ? summary.selfDoctorId : null;
  const upcomingToday = todayList
    .filter((a) => !selfDoctorId || a.doctorId === selfDoctorId)
    .filter((a) => a.status === "REQUESTED" || a.status === "CONFIRMED" || a.status === "CHECKED_IN")
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, 8);

  return (
    <div>
      <PageHeader
        title={todayHeading(new Date())}
        description="Lịch khám, thu tiền và cảnh báo của riêng hôm nay. Số liệu theo kỳ nằm ở mục Báo cáo."
        actions={
          periodLink && (
            <Button asChild variant="outline" size="sm">
              <Link to={periodLink}>
                Xem theo kỳ
                <ArrowRight />
              </Link>
            </Button>
          )
        }
      />

      {showAppointmentKpis && summaryQuery.isSuccess && (
        <p className="mb-2 text-xs text-muted-foreground" data-testid="kpi-freshness">
          Cập nhật lúc {formatTime(new Date(summaryQuery.dataUpdatedAt).toISOString())}
          {scopeLabel && <> · {scopeLabel}</>}
          {mine && <> · Số của tôi</>}
        </p>
      )}
      {/* #19869 — thẻ Thực thu là số theo phạm vi chi nhánh. Dòng trên đã nói phạm vi khi có ô lịch
          hẹn; người chỉ có quyền tài chính không có dòng đó, nên nhãn phạm vi đứng ở đây. */}
      {showRevenue && !showAppointmentKpis && <ReportScopeNotice className="mb-2" />}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {showAppointmentKpis && (
          <>
            <KpiCard
              icon={CalendarDays}
              // #19906 — "còn hiệu lực", không phải "lịch hẹn": ô này KHÔNG đếm lịch đã huỷ, còn
              // "Lịch theo ngày khám" của Tổng quan vận hành (K-01) thì có. Cùng một ngày, hai nhãn
              // gần nhau cho hai con số khác nhau là thứ người dùng sẽ báo lỗi.
              label={mine ? "Lịch còn hiệu lực của tôi" : "Lịch còn hiệu lực"}
              to={listLink("active")}
              loading={summaryQuery.isLoading}
              failed={summaryQuery.isError}
              // #19850 giữ nguyên: ô này KHÔNG đếm lịch đã huỷ. Danh sách đích lọc theo ngày nên
              // vẫn hiện các lịch huỷ — khớp tuyệt đối là việc của drill-down (STAT-30).
              value={String((summary?.totals.total ?? 0) - (summary?.totals.cancelled ?? 0))}
              hint={
                summary && summary.totals.cancelled > 0
                  ? `Không gồm ${summary.totals.cancelled} lịch đã huỷ`
                  : "Không gồm lịch đã huỷ"
              }
            />
            <KpiCard
              icon={Activity}
              label="Đang chờ khám"
              to={listLink("waiting_now")}
              loading={summaryQuery.isLoading}
              failed={summaryQuery.isError}
              // Review: số ĐANG chờ tính cả bệnh nhân vừa chuyển khoa (họ được tạo ở CHECKED_IN).
              value={String(summary?.waitingNow ?? 0)}
              hint={`Đã tiếp đón · ${
                (summary?.totals.requested ?? 0) + (summary?.totals.confirmed ?? 0)
              } chưa đến`}
            />
            <KpiCard
              icon={CircleCheck}
              label="Đã hoàn thành"
              to={listLink("completed")}
              loading={summaryQuery.isLoading}
              failed={summaryQuery.isError}
              value={String(summary?.totals.completed ?? 0)}
              hint={
                summary && summary.totals.noShow > 0
                  ? `${summary.totals.noShow} không đến`
                  : "Đã ký bệnh án"
              }
            />
          </>
        )}
        {showRevenue && (
          <KpiCard
            icon={Wallet}
            label="Thực thu hôm nay"
            // #19900 — con số doanh thu DUY NHẤT của Tổng quan; chi tiết (cả "phát sinh") ở báo cáo.
            to={REVENUE_REPORT_PATH}
            value={revenueQuery.isError ? "—" : formatVnd(revenueToday) + "₫"}
            hint={revenueQuery.isError ? "Không tải được số liệu" : "So với hôm qua"}
            delta={
              revenueDelta === null
                ? { dir: "flat", text: "—" }
                : revenueDelta === 0
                ? { dir: "flat", text: "0%" }
                : revenueDelta > 0
                  ? { dir: "up", text: `+${revenueDelta}%` }
                  : { dir: "down", text: `${revenueDelta}%` }
            }
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 py-4">
              <CardTitle className="text-base">
                {selfDoctorId ? "Sắp tới hôm nay của tôi" : "Sắp tới hôm nay"}
              </CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/appointments/day">
                  Mở lịch ngày
                  <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <DataTable
                ariaLabel="Lịch hẹn sắp tới hôm nay"
                columns={upcomingColumns}
                data={upcomingToday}
                loading={todayQuery.isLoading}
                error={todayQuery.error}
                density="compact"
                empty={{
                  title: "Hôm nay không còn lịch hẹn nào",
                  description:
                    "Toàn bộ lịch hẹn đã hoàn thành hoặc kết thúc — chúc một ngày làm việc nhẹ nhàng.",
                }}
              />
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-4">
          {quickActions.length > 0 && (
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base">Thao tác nhanh</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 pt-0">
                {quickActions.map((action) => (
                  <QuickAction key={action.to} {...action} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* #19859 — cảnh báo chỉ hiện khi có SỐ của server để dựa vào. Không có số thì không có
              thẻ: "Không có cảnh báo hôm nay" khi không ai đếm là một khẳng định sai. */}
          {/* #19867 (review): chờ CẢ cảnh báo theo ngưỡng — vẽ khi nó còn đang tải là để thẻ nói
              "Không có cảnh báo hôm nay" trong khi 12 bệnh án chưa ký chưa kịp về. */}
          {showAppointmentKpis &&
            summaryQuery.isSuccess &&
            summary &&
            (alertsQuery.isSuccess || alertsQuery.isError) && (
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 py-4">
                <CardTitle className="text-base">Cảnh báo</CardTitle>
                {/* #19867: ngưỡng do phòng khám đặt — cấu hình phòng khám (clinic.write). */}
                {perms.has("clinic.write") && (
                  <Button variant="ghost" size="sm" onClick={() => setEditingThresholds(true)}>
                    Ngưỡng
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <TodayAlerts
                  late={summary.lateNotArrived}
                  noRoom={summary.noRoom}
                  noShow={summary.totals.noShow}
                  links={{
                    late: listLink("late"),
                    noRoom: listLink("no_room"),
                    noShow: listLink("no_show"),
                  }}
                  dataAlerts={alertsQuery.data?.alerts ?? []}
                  dataFailed={alertsQuery.isError}
                  dataAlertLink={(a) => dataAlertLink(a, alertsQuery.data?.today ?? today)}
                />
              </CardContent>
            </Card>
          )}

          {editingThresholds && (
            <AlertSettingsDialog onClose={() => setEditingThresholds(false)} />
          )}

          {resourceLinks.length > 0 && (
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base">Tài nguyên</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-1.5 text-sm">
                  {resourceLinks.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="text-primary hover:underline">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

const WEEKDAYS = ["Chủ nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];

/**
 * #19901 — tiêu đề nói ra phạm vi của màn: "Hôm nay · Thứ Hai, 26/10/2026". Không bộ lọc ngày nào
 * ở đây là cố ý (các ô "đang chờ", "quá giờ" chỉ có nghĩa lúc này), nên chính tiêu đề phải nói thế.
 * Ngày theo giờ địa phương (#19847), dựng tay để không phụ thuộc cách ICU viết hoa thứ.
 */
export function todayHeading(now: Date): string {
  const [y, m, d] = isoDate(now).split("-");
  return `Hôm nay · ${WEEKDAYS[now.getDay()]}, ${d}/${m}/${y}`;
}

/**
 * "Chi nhánh đang xem" cho dòng cập nhật. Phòng khám một chi nhánh: không ghi gì (không có gì để
 * phân biệt). Có ghim chi nhánh: tên chi nhánh đó. Không ghim: báo cáo đọc MỌI chi nhánh người xem
 * được phép (ReportScope, #19842), nên ghi đúng như vậy — không giả vờ là chi nhánh đầu danh sách
 * như ô chọn ở thanh trên.
 */
function branchScopeLabel(list: { id: string; name: string; status: string }[] | undefined) {
  const active = (list ?? []).filter((b) => b.status === "ACTIVE");
  if (active.length <= 1) return null;
  const pinned = localStorage.getItem(ACTIVE_BRANCH_KEY);
  const hit = pinned ? active.find((b) => b.id === pinned) : undefined;
  return hit ? `Chi nhánh ${hit.name}` : "Mọi chi nhánh được phép";
}

/** Ba cảnh báo dựa trên số của server (#19859). Không có gì → một dòng xanh. */
function TodayAlerts({
  late,
  noRoom,
  noShow,
  links,
  dataAlerts = [],
  dataAlertLink,
  dataFailed = false,
}: {
  late: number;
  noRoom: number;
  noShow: number;
  /** Drill-down tới đúng các lịch tạo nên từng con số (#19863). */
  links: { late: string; noRoom: string; noShow: string };
  /** Cảnh báo theo ngưỡng của phòng khám, server tính (#19867). */
  dataAlerts?: ReportAlert[];
  dataAlertLink?: (a: ReportAlert) => string;
  /** Không tải được cảnh báo theo ngưỡng: KHÔNG được nói "không có cảnh báo". */
  dataFailed?: boolean;
}) {
  const items: { key: string; to: string; text: ReactNode }[] = [];
  const nf = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 1 });
  for (const a of dataAlerts) {
    const to = dataAlertLink ? dataAlertLink(a) : "#";
    if (a.code === "PENDING_COMPLETION")
      items.push({
        key: "pendingCompletion",
        to,
        text: (
          <>
            <strong>Bệnh án chưa ký: {nf.format(a.value)}.</strong> Đã tiếp đón ở các ngày trước
            mà lượt khám chưa hoàn tất.
          </>
        ),
      });
    if (a.code === "WAIT_MEDIAN")
      items.push({
        key: "waitMedian",
        to,
        text: (
          <>
            <strong>Chờ khám trung vị hôm nay {nf.format(a.value)} phút</strong> — vượt ngưỡng{" "}
            {nf.format(a.threshold ?? 0)} phút.
          </>
        ),
      });
    if (a.code === "NO_SHOW_RISE")
      items.push({
        key: "noShowRise",
        to,
        text: (
          <>
            <strong>Tỷ lệ vắng 7 ngày qua {nf.format(a.current ?? 0)}%</strong> — tăng{" "}
            {nf.format(a.value)} điểm so với 4 tuần trước ({nf.format(a.baseline ?? 0)}%).
          </>
        ),
      });
  }
  if (late > 0)
    items.push({
      key: "late",
      to: links.late,
      text: (
        <>
          <strong>Quá giờ chưa tới: {late}.</strong> Đã qua giờ hẹn mà bệnh nhân chưa được tiếp
          đón.
        </>
      ),
    });
  if (noRoom > 0)
    items.push({
      key: "noRoom",
      to: links.noRoom,
      text: (
        <>
          <strong>Chưa phân phòng: {noRoom}.</strong> Các ca này chưa hiện trên màn khám của bác sĩ
          nào.
        </>
      ),
    });
  if (noShow > 0)
    items.push({
      key: "noShow",
      to: links.noShow,
      text: (
        <>
          <strong>{noShow} bệnh nhân không đến.</strong> Lễ tân nên gọi lại để xác nhận lý do.
        </>
      ),
    });
  const failedNote = dataFailed && (
    <div
      className="flex items-start gap-2 rounded-md border border-muted p-3 text-sm text-muted-foreground"
      data-testid="alerts-failed"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>Không tải được cảnh báo theo ngưỡng (bệnh án chưa ký, thời gian chờ, tỷ lệ vắng).</div>
    </div>
  );
  if (items.length === 0 && dataFailed) {
    return failedNote;
  }
  if (items.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div>Không có cảnh báo hôm nay.</div>
      </div>
    );
  }
  return (
    <>
      {items.map((it) => (
        <Link
          key={it.key}
          to={it.to}
          data-testid={`alert-${it.key}`}
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 hover:bg-amber-100"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>{it.text}</div>
        </Link>
      ))}
      {failedNote}
    </>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  delta,
  to,
  loading,
  failed,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  delta?: { dir: "up" | "down" | "flat"; text: string };
  /** Danh sách tạo nên con số — cùng điều kiện (#19859). */
  to?: string;
  loading?: boolean;
  failed?: boolean;
}) {
  // Đang tải / lỗi KHÔNG hiện "0": số 0 là một khẳng định về phòng khám.
  const shown = loading ? "…" : failed ? "—" : value;
  const note = failed ? "Không tải được số liệu" : hint;
  const body = (
    <Card className={to ? "transition-colors hover:border-primary/40" : undefined}>
      <CardContent className="flex items-start gap-2.5 p-4 sm:gap-3 sm:p-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-10 sm:w-10">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className="text-xl font-bold text-foreground sm:text-2xl"
              aria-busy={loading || undefined}
            >
              {shown}
            </span>
            {delta && delta.dir !== "flat" && (
              <Badge variant={delta.dir === "up" ? "success" : "destructive"}>
                {delta.dir === "up" ? (
                  <TrendingUp className="mr-0.5 h-3 w-3" aria-hidden="true" />
                ) : (
                  <TrendingDown className="mr-0.5 h-3 w-3" aria-hidden="true" />
                )}
                {delta.text}
              </Badge>
            )}
            {delta && delta.dir === "flat" && (
              <Badge variant="muted">
                <Minus className="mr-0.5 h-3 w-3" aria-hidden="true" />
                {delta.text}
              </Badge>
            )}
          </div>
          <div className="mt-1 truncate text-sm font-medium text-muted-foreground">
            {label}
          </div>
          {note && (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{note}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  return to && !loading && !failed ? (
    <Link to={to} aria-label={`${label}: ${value}`} className="block rounded-xl">
      {body}
    </Link>
  ) : (
    body
  );
}

function QuickAction({
  to,
  icon: Icon,
  title,
  sub,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  sub: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-md border border-transparent p-2.5 transition-colors hover:border-input hover:bg-muted"
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="truncate text-xs text-muted-foreground">{sub}</div>
      </div>
    </Link>
  );
}
